import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useMixerModel } from '../../audio/useMixerModel';
import { VuMeter } from '../../components/VuMeter';
import { S, t, tList, type Leaf } from '../../i18n/strings';
import { cx } from '../../lib/cx';
import { useActions, useSteve } from '../../state/context';
import { useAudioText, useGuestbookLines } from '../../state/derived';
import { formatSize, publish, revisionDate, seedRevisions, type Revision } from './history';
import { WikiSteve } from './WikiSteve';
import { INSERTS, applyInsert, initialWikitext } from './wikitext';
import styles from './wiki.module.css';

/** Les quatre onglets. « Article » et « Discussion » d'un côté, la lecture de l'autre. */
type Tab = 'read' | 'talk' | 'edit' | 'history';

/** Le sous-titre porté par chaque onglet. */
const SUBTITLES = {
  read: 'sub_read',
  talk: 'sub_talk',
  edit: 'sub_edit',
  history: 'sub_history',
} as const;

/** Un appel de note : le petit exposant bleu entre crochets. */
function Ref({ n }: { readonly n: number }): ReactNode {
  return (
    <sup className={styles.ref}>
      <a href={`#note-${String(n)}`}>[{n}]</a>
    </sup>
  );
}

/** Un modèle de maintenance en ligne — « réf. nécessaire », « précision nécessaire ». */
function Mark({ label }: { readonly label: string }): ReactNode {
  return <sup className={styles.mark}>[{label}]</sup>;
}

/** Un lien rouge : la page n'existe pas, et son `title` le dit. */
function RedLink({ children, title }: { readonly children: ReactNode; readonly title: string }): ReactNode {
  return (
    <a className={styles.redLink} href="#" title={title} onClick={(event) => { event.preventDefault(); }}>
      {children}
    </a>
  );
}

/**
 * Skin 10 — Poissonpédia : une page d'encyclopédie complète.
 *
 * **C'est le seul skin dont la blague repose sur le sérieux.** La forme est
 * irréprochable — gris, bleus de liens, serif, aucun arrondi, aucune ombre,
 * aucune animation — et tout le comique est dans le texte et dans les
 * mécanismes : bandeaux de maintenance, appels de note, liens rouges, guerre
 * d'édition, commentaires cachés dans le wikitexte. Ajouter une couleur, une
 * icône décorative ou une transition tue le skin.
 *
 * Deux conséquences directes dans ce fichier : le Steve-curseur est coupé pour
 * ce skin (voir `App.tsx`), et le dessin de Steve y est **figé**, posé comme une
 * image téléversée.
 */
export function WikiSkin(): ReactNode {
  const { lang, seen, draft, clock } = useSteve();
  const actions = useActions();
  const mixer = useMixerModel();
  const audio = useAudioText();
  const guestbook = useGuestbookLines();

  const [tab, setTab] = useState<Tab>('read');
  const [history, setHistory] = useState<readonly Revision[]>(seedRevisions);
  const [wikitext, setWikitext] = useState(() => initialWikitext(lang));
  const [summary, setSummary] = useState('');
  const [notice, setNotice] = useState<Leaf | null>(null);
  const [diff, setDiff] = useState(false);
  const areaRef = useRef<HTMLTextAreaElement>(null);

  const title = t(S.wiki.titles[tab], lang);
  const subtitle = t(S.wiki.titles[SUBTITLES[tab]], lang, {
    n: tab === 'talk' ? guestbook.length : history.length,
  });

  /*
   * « Rechercher » et « Article au hasard » relancent le tirage de skin.
   *
   * C'est la seule sortie du skin, et elle est diégétique : sur une
   * encyclopédie à un seul article, chercher autre chose ne peut mener qu'ailleurs.
   */
  const leave = (): void => {
    actions.cycleSkin(0);
  };

  const onInsert = (index: number): void => {
    const insert = INSERTS[index];
    const area = areaRef.current;
    if (!insert || !area) return;
    const applied = applyInsert(wikitext, area.selectionStart, area.selectionEnd, insert);
    setWikitext(applied.text);
    // Le champ garde le focus et la sélection : sans ça, l'exemple posé n'est
    // pas remplaçable en tapant par-dessus, ce qui est tout l'intérêt.
    window.requestAnimationFrame(() => {
      area.focus();
      area.setSelectionRange(applied.start, applied.end);
    });
  };

  const onPublish = (): void => {
    setHistory((current) => publish(current, summary));
    setNotice(summary.trim() === '' ? S.wiki.edit.published_without : S.wiki.edit.published_with);
    setSummary('');
    setTab('history');
  };

  const sidebar = useMemo(
    () =>
      [
        { title: S.wiki.sidebar.nav, items: tList(S.wiki.sidebar.nav_items, lang), random: 2 },
        { title: S.wiki.sidebar.contribute, items: tList(S.wiki.sidebar.contribute_items, lang), random: -1 },
        { title: S.wiki.sidebar.tools, items: tList(S.wiki.sidebar.tools_items, lang), random: -1 },
      ] as const,
    [lang],
  );

  return (
    <div className={styles.root}>
      <header className={styles.masthead}>
        <span className={styles.logo} aria-hidden="true" />
        <div className={styles.brand}>
          <span className={styles.siteName}>{t(S.wiki.site.name, lang)}</span>
          <span className={styles.tagline}>{t(S.wiki.site.tagline, lang)}</span>
        </div>
        <div className={styles.searchBox}>
          <input
            type="search"
            className={styles.search}
            placeholder={t(S.wiki.site.search, lang)}
            aria-label={t(S.wiki.site.search, lang)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') leave();
            }}
          />
          <button type="button" className={styles.searchButton} onClick={leave}>
            {t(S.wiki.site.search_btn, lang)}
          </button>
        </div>
        <div className={styles.account}>
          <button type="button" className={styles.link} onClick={leave}>
            {t(S.wiki.site.create_account, lang)}
          </button>
          <button type="button" className={styles.link} onClick={leave}>
            {t(S.wiki.site.login, lang)}
          </button>
        </div>
      </header>

      <div className={styles.columns}>
        <nav className={styles.sidebar} aria-label={t(S.wiki.sidebar.nav, lang)}>
          {sidebar.map((block) => (
            <div key={block.title.fr} className={styles.sideBlock}>
              <h2 className={styles.sideTitle}>{t(block.title, lang)}</h2>
              <ul className={styles.sideList}>
                {block.items.map((item, index) => (
                  <li key={item}>
                    <button
                      type="button"
                      className={styles.link}
                      onClick={index === block.random ? leave : () => { setTab('read'); }}
                    >
                      {item}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className={styles.sideBlock}>
            <h2 className={styles.sideTitle}>{t(S.wiki.site.langs, lang)}</h2>
            <ul className={styles.sideList}>
              {['Français', 'English', 'Deutsch', 'Español', 'Italiano', '日本語', 'Nederlands'].map(
                (name) => (
                  <li key={name}>
                    <button type="button" className={styles.link} onClick={leave}>
                      {name}
                    </button>
                  </li>
                ),
              )}
              <li className={styles.langsMore}>{t(S.wiki.site.langs_more, lang)}</li>
            </ul>
          </div>
        </nav>

        <main className={styles.page}>
          <div className={styles.tabs}>
            <div className={styles.tabGroup}>
              {(['read', 'talk'] as const).map((id) => (
                <button
                  key={id}
                  type="button"
                  className={cx(styles.tab, (tab === id || (id === 'read' && tab !== 'talk')) && styles.tabOn)}
                  onClick={() => {
                    setTab(id);
                  }}
                >
                  {t(id === 'read' ? S.wiki.tabs.article : S.wiki.tabs.talk, lang)}
                </button>
              ))}
            </div>
            <div className={cx(styles.tabGroup, styles.tabGroupRight)}>
              {(['read', 'edit', 'history'] as const).map((id) => (
                <button
                  key={id}
                  type="button"
                  className={cx(styles.tab, tab === id && styles.tabOn)}
                  onClick={() => {
                    setTab(id);
                  }}
                >
                  {t(S.wiki.tabs[id === 'read' ? 'read' : id], lang)}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.body}>
            <h1 className={styles.h1}>{title}</h1>
            <p className={styles.subtitle}>{subtitle}</p>

            {tab === 'read' && (
              <Article
                lang={lang}
                seen={seen}
                mixer={mixer}
                audio={audio}
                onToggleAudio={actions.toggleAudio}
                onLeave={leave}
                onTab={setTab}
              />
            )}

            {tab === 'talk' && (
              <>
                <Notice
                  kind="blue"
                  glyph="✎"
                  title={t(S.wiki.notices.talk_t, lang)}
                  body={t(S.wiki.notices.talk_b, lang)}
                />
                <ul className={styles.topics}>
                  {guestbook.map((entry) => (
                    <li key={entry.id} className={styles.topic}>
                      <h2 className={styles.topicTitle}>
                        {entry.who}{' '}
                        <button type="button" className={styles.smallLink} onClick={() => { setTab('edit'); }}>
                          [{t(S.wiki.talk.reply, lang)}]
                        </button>
                      </h2>
                      <div className={styles.topicBody}>
                        <p className={styles.paragraph}>{entry.text}</p>
                        {/* Les quatre tildes ne sont pas remplacés : c'est la blague. */}
                        <p className={styles.signature}>— ~~~~</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <h2 className={styles.h2}>{t(S.wiki.talk.add_topic, lang)}</h2>
                <textarea
                  className={styles.textarea}
                  rows={4}
                  value={draft}
                  placeholder={t(S.wiki.talk.placeholder, lang)}
                  aria-label={t(S.wiki.talk.add_topic, lang)}
                  onChange={(event) => {
                    actions.setDraft(event.currentTarget.value);
                  }}
                />
                <div className={styles.formRow}>
                  <button type="button" className={styles.primary} onClick={actions.postEntry}>
                    {t(S.wiki.talk.publish, lang)}
                  </button>
                  <button
                    type="button"
                    className={styles.button}
                    onClick={() => {
                      actions.setDraft('');
                    }}
                  >
                    {t(S.wiki.talk.cancel, lang)}
                  </button>
                </div>
              </>
            )}

            {tab === 'edit' && (
              <>
                <Notice
                  kind="blue"
                  glyph="✎"
                  title={t(S.wiki.notices.edit_t, lang)}
                  body={t(S.wiki.notices.edit_b, lang)}
                />
                <div className={styles.toolbar} role="toolbar" aria-label={t(S.wiki.tabs.edit, lang)}>
                  {INSERTS.map((insert, index) => (
                    <button
                      key={insert.id}
                      type="button"
                      className={styles.toolButton}
                      title={t(insert.label, lang)}
                      onClick={() => {
                        onInsert(index);
                      }}
                    >
                      {insert.glyph}
                    </button>
                  ))}
                </div>
                <textarea
                  ref={areaRef}
                  className={cx(styles.textarea, styles.wikitext)}
                  rows={22}
                  value={wikitext}
                  aria-label={t(S.wiki.tabs.edit, lang)}
                  onChange={(event) => {
                    setWikitext(event.currentTarget.value);
                  }}
                />
                <p className={styles.reminder}>{t(S.wiki.edit.reminder, lang, { n: seen })}</p>
                <label className={styles.summaryLabel} htmlFor="wiki-summary">
                  {t(S.wiki.edit.summary_label, lang)}
                </label>
                <input
                  id="wiki-summary"
                  type="text"
                  className={styles.input}
                  value={summary}
                  placeholder={t(S.wiki.edit.summary_placeholder, lang)}
                  onChange={(event) => {
                    setSummary(event.currentTarget.value);
                  }}
                />
                <div className={styles.formRow}>
                  <button type="button" className={styles.primary} onClick={onPublish}>
                    {t(S.wiki.edit.publish, lang)}
                  </button>
                  <button type="button" className={styles.button} onClick={() => { setTab('read'); }}>
                    {t(S.wiki.edit.preview, lang)}
                  </button>
                  <button type="button" className={styles.button} onClick={() => { setTab('history'); }}>
                    {t(S.wiki.edit.show_changes, lang)}
                  </button>
                </div>
              </>
            )}

            {tab === 'history' && (
              <>
                {notice && <p className={styles.published}>{t(notice, lang)}</p>}
                <p className={styles.paragraph}>{t(S.wiki.history.intro, lang)}</p>
                <div className={styles.formRow}>
                  <button
                    type="button"
                    className={styles.button}
                    onClick={() => {
                      setDiff(true);
                    }}
                  >
                    {t(S.wiki.history.compare, lang)}
                  </button>
                </div>
                {diff && <p className={styles.diffResult}>{t(S.wiki.history.diff_result, lang)}</p>}
                <ul className={styles.revisions}>
                  {history.map((revision, index) => (
                    <li key={revision.id} className={styles.revision}>
                      <span className={styles.revLinks}>
                        (
                        <button type="button" className={styles.smallLink} onClick={() => { setDiff(true); }}>
                          {t(S.wiki.history.cur, lang)}
                        </button>
                        {' | '}
                        <button type="button" className={styles.smallLink} onClick={() => { setDiff(true); }}>
                          {t(S.wiki.history.diff, lang)}
                        </button>
                        )
                      </span>
                      <input
                        type="radio"
                        name="wiki-older"
                        aria-label={`${t(S.wiki.history.diff, lang)} — ${String(revision.id)}`}
                        defaultChecked={index === 1}
                      />
                      <input
                        type="radio"
                        name="wiki-newer"
                        aria-label={`${t(S.wiki.history.cur, lang)} — ${String(revision.id)}`}
                        defaultChecked={index === 0}
                      />
                      <span className={styles.revDate}>{revisionDate(revision.daysAgo, lang)}</span>
                      <span className={styles.revUser}>{t(revision.user, lang)}</span>
                      <span className={styles.revSize}>{formatSize(revision.size, lang)}</span>
                      <span className={styles.revComment}>{t(revision.comment, lang)}</span>
                    </li>
                  ))}
                </ul>
                <p className={styles.total}>{t(S.wiki.history.total, lang, { n: history.length })}</p>
              </>
            )}
          </div>

          <footer className={styles.footer}>
            <p>{t(S.wiki.footer.last_modified, lang, { date: today(lang), time: clock })}</p>
            <p>{t(S.wiki.footer.credits, lang)}</p>
            <p>
              <button type="button" className={styles.link} onClick={leave}>
                {t(S.wiki.footer.privacy, lang)}
              </button>
              {' · '}
              {t(S.common.egg_hint, lang)}
            </p>
          </footer>
        </main>
      </div>
    </div>
  );
}

/** La date du jour, au format long de la langue servie. */
function today(lang: 'fr' | 'en'): string {
  return new Date().toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

interface NoticeProps {
  readonly kind: 'yellow' | 'blue';
  readonly glyph: string;
  readonly title: string;
  readonly body: string;
  readonly link?: ReactNode;
}

/**
 * Un bandeau de maintenance.
 *
 * `role="presentation"` sur le tableau : c'est une mise en page, pas des
 * données, et un lecteur d'écran qui l'annoncerait comme un tableau de deux
 * cellules ferait perdre la phrase.
 */
function Notice({ kind, glyph, title, body, link }: NoticeProps): ReactNode {
  return (
    <table className={cx(styles.notice, kind === 'blue' && styles.noticeBlue)} role="presentation">
      <tbody>
        <tr>
          <td className={styles.noticeGlyph} aria-hidden="true">
            {glyph}
          </td>
          <td className={styles.noticeBody}>
            <strong className={styles.noticeTitle}>{title}</strong>
            <span className={styles.noticeText}>
              {body} {link}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

interface ArticleProps {
  readonly lang: 'fr' | 'en';
  readonly seen: number;
  readonly mixer: ReturnType<typeof useMixerModel>;
  readonly audio: ReturnType<typeof useAudioText>;
  readonly onToggleAudio: () => void;
  readonly onLeave: () => void;
  readonly onTab: (tab: Tab) => void;
}

/** L'onglet « Lire » : l'article lui-même, dans l'ordre du handoff. */
function Article({ lang, seen, mixer, audio, onToggleAudio, onLeave, onTab }: ArticleProps): ReactNode {
  const B = S.wiki.body;
  const table = S.wiki.locomotion_table;

  return (
    <>
      <Notice
        kind="yellow"
        glyph="⚠"
        title={t(S.wiki.notices.single_source_t, lang)}
        body={t(S.wiki.notices.single_source_b, lang)}
        link={
          <button type="button" className={styles.smallLink} onClick={onLeave}>
            {t(S.wiki.notices.single_source_a, lang)}
          </button>
        }
      />
      <Notice
        kind="yellow"
        glyph="⚖"
        title={t(S.wiki.notices.neutral_t, lang)}
        body={t(S.wiki.notices.neutral_b, lang)}
        link={
          <button type="button" className={styles.smallLink} onClick={() => { onTab('talk'); }}>
            {t(S.wiki.marks.talk_link, lang)}
          </button>
        }
      />
      <Notice
        kind="blue"
        glyph="🔭"
        title={t(S.wiki.notices.unverifiable_t, lang)}
        body={t(S.wiki.notices.unverifiable_b, lang)}
      />

      <aside className={styles.infobox}>
        <h2 className={styles.infoTitle}>{t(S.wiki.titles.read, lang)}</h2>
        <div className={styles.infoImage}>
          <WikiSteve scale={0.66} caption={t(S.wiki.infobox.caption, lang)} />
        </div>
        <InfoSection label={t(S.wiki.infobox.classification, lang)} />
        <InfoRow k={t(S.wiki.infobox.kingdom, lang)} v="Animalia" />
        <InfoRow k={t(S.wiki.infobox.phylum, lang)} v="Chordata" />
        <InfoRow k={t(S.wiki.infobox.class, lang)} v="Actinopterygii" />
        <InfoRow k={t(S.wiki.infobox.order, lang)} v="incertae sedis" />
        <InfoRow k={t(S.wiki.infobox.family, lang)} v="Steveidae" />
        <InfoRow k={t(S.wiki.infobox.genus, lang)} v="Steveus" />
        <InfoRow k={t(S.wiki.infobox.binomial, lang)} v="Steveus orangeus" />
        <InfoRow k={t(S.wiki.infobox.individuals, lang)} v="1" />
        <InfoSection label={t(S.wiki.infobox.traits, lang)} />
        <InfoRow k={t(S.wiki.infobox.colour, lang)} v={t(S.wiki.infobox.colour_v, lang)} />
        <InfoRow k={t(S.wiki.infobox.arms, lang)} v="2" />
        <InfoRow k={t(S.wiki.infobox.legs, lang)} v="2" />
        <InfoRow k={t(S.wiki.infobox.eyes, lang)} v="2" />
        <InfoRow k={t(S.wiki.infobox.dorsal, lang)} v={t(S.wiki.infobox.dorsal_v, lang)} />
        <InfoRow k={t(S.wiki.infobox.size, lang)} v={t(S.wiki.infobox.size_v, lang)} />
        <InfoRow k={t(S.wiki.infobox.beauty, lang)} v={t(S.wiki.infobox.beauty_v, lang)} />
        <InfoRow k={t(S.wiki.infobox.smell, lang)} v={t(S.wiki.infobox.smell_v, lang)} />
        <InfoSection label={t(S.wiki.infobox.locomotion, lang)} />
        <InfoRow k={t(S.wiki.infobox.on_land, lang)} v={t(S.wiki.infobox.on_land_v, lang)} />
        <InfoRow k={t(S.wiki.infobox.in_water, lang)} v={t(S.wiki.infobox.in_water_v, lang)} />
        <InfoRow k={t(S.wiki.infobox.in_air, lang)} v={t(S.wiki.infobox.undocumented, lang)} />
        <InfoSection label={t(S.wiki.infobox.status, lang)} />
        <InfoRow k={t(S.wiki.infobox.conservation, lang)} v={t(S.wiki.infobox.conservation_v, lang)} />
        <InfoSection label={t(S.wiki.infobox.credits, lang)} />
        <InfoRow
          k={t(S.wiki.infobox.drawing, lang)}
          v={
            <a
              className={styles.link}
              href="https://www.instagram.com/vigzvigz/?hl=fr"
              target="_blank"
              rel="noopener noreferrer"
            >
              Vigz
            </a>
          }
        />
        <InfoRow
          k={t(S.wiki.infobox.music, lang)}
          v={
            <a
              className={styles.link}
              href="https://www.youtube.com/@tomomp3"
              target="_blank"
              rel="noopener noreferrer"
            >
              Tomo
            </a>
          }
        />
        <InfoRow k={t(S.wiki.infobox.views, lang)} v={String(seen)} />
      </aside>

      <p className={styles.hatnote}>{t(S.wiki.hatnote, lang)}</p>

      <p className={styles.paragraph}>
        <strong>{t(S.wiki.titles.read, lang)}</strong> {t(B.lead1, lang)}
        <Ref n={1} />
      </p>
      <p className={styles.paragraph}>
        {t(B.lead2, lang)}
        <Ref n={1} />
        <Mark label={t(S.wiki.marks.citation, lang)} />
      </p>
      <p className={styles.paragraph}>
        {t(B.lead3, lang)}
        <Ref n={1} />
      </p>

      <Toc lang={lang} />

      <Section id="etymology" title={t(S.wiki.sections.etymology, lang)} lang={lang} onTab={onTab}>
        <p className={styles.paragraph}>
          {t(B.etymology1, lang)}
          <Mark label={t(S.wiki.marks.citation, lang)} />
        </p>
        <p className={styles.paragraph}>
          {t(B.etymology2, lang)}
          <Ref n={3} />
        </p>
      </Section>

      <Section id="description" title={t(S.wiki.sections.description, lang)} lang={lang} onTab={onTab}>
        <div className={styles.thumb}>
          <WikiSteve scale={0.42} caption={t(B.thumb_caption, lang)} />
        </div>
        <p className={styles.paragraph}>
          {t(B.description1, lang)}
          <Ref n={1} />
        </p>
        <h3 className={styles.h3}>{t(S.wiki.sections.arms_legs, lang)}</h3>
        <p className={styles.paragraph}>
          {t(B.arms1, lang)}
          <Ref n={4} />
        </p>
        <p className={styles.paragraph}>
          {t(B.arms2, lang)}
          <Ref n={1} />
        </p>
        <h3 className={styles.h3}>{t(S.wiki.sections.colour, lang)}</h3>
        <p className={styles.paragraph}>
          {t(B.colour1, lang)}{' '}
          <button type="button" className={styles.smallLink} onClick={() => { onTab('history'); }}>
            {t(S.wiki.marks.history_link, lang)}
          </button>
        </p>
        <h3 className={styles.h3}>{t(S.wiki.sections.eyes, lang)}</h3>
        <p className={styles.paragraph}>
          {t(B.eyes1, lang)}
          <Ref n={3} />
        </p>
        <p className={styles.paragraph}>
          {t(B.eyes2, lang)}
          <Ref n={1} />
        </p>
        <h3 className={styles.h3}>{t(S.wiki.sections.beauty, lang)}</h3>
        <p className={styles.paragraph}>
          {t(B.beauty1, lang)}
          <Ref n={1} />
        </p>
      </Section>

      <Section id="locomotion" title={t(S.wiki.sections.locomotion, lang)} lang={lang} onTab={onTab}>
        <p className={styles.paragraph}>
          {t(B.locomotion1, lang)}
          <Ref n={1} />
        </p>
        <div className={styles.tableScroll}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>{t(table.medium, lang)}</th>
                <th>{t(table.observed, lang)}</th>
                <th>{t(table.expected, lang)}</th>
                <th>{t(table.gap, lang)}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t(table.land, lang)}</td>
                <td>{t(table.swims, lang)}</td>
                <td>{t(table.walks, lang)}</td>
                <td className={styles.gapTotal}>{t(table.total, lang)}</td>
              </tr>
              <tr>
                <td>{t(table.water, lang)}</td>
                <td>{t(table.walks, lang)}</td>
                <td>{t(table.swims, lang)}</td>
                <td className={styles.gapTotal}>{t(table.total, lang)}</td>
              </tr>
              <tr>
                <td>{t(table.air, lang)}</td>
                <td>{t(S.wiki.infobox.undocumented, lang)}</td>
                <td>{t(table.falls, lang)}</td>
                <td>{t(table.unknown, lang)}</td>
              </tr>
              <tr>
                <td>{t(table.soft, lang)}</td>
                <td>{t(S.wiki.infobox.undocumented, lang)}</td>
                <td>{t(S.wiki.infobox.undocumented, lang)}</td>
                <td className={styles.gapNone}>{t(table.none, lang)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <h3 className={styles.h3}>{t(S.wiki.sections.land_swim, lang)}</h3>
        <p className={styles.paragraph}>
          {t(B.land1, lang)}
          <Mark label={t(S.wiki.marks.precision, lang)} />
        </p>
        <h3 className={styles.h3}>{t(S.wiki.sections.water_walk, lang)}</h3>
        <p className={styles.paragraph}>
          {t(B.water1, lang)}
          <Mark label={t(S.wiki.marks.citation, lang)} />
        </p>
      </Section>

      <Section id="smell" title={t(S.wiki.sections.smell, lang)} lang={lang} onTab={onTab}>
        <p className={styles.paragraph}>
          {t(B.smell1, lang)}
          <Ref n={1} />
        </p>
        <p className={styles.paragraph}>
          {t(B.smell2, lang)}
          <Ref n={3} />
        </p>
      </Section>

      <Section id="affection" title={t(S.wiki.sections.affection, lang)} lang={lang} onTab={onTab}>
        <p className={styles.paragraph}>
          {t(B.affection1, lang)}
          <Ref n={3} />
        </p>
        <p className={styles.paragraph}>{t(B.affection2, lang, { n: seen })}</p>
      </Section>

      <Section id="song" title={t(S.wiki.sections.song, lang)} lang={lang} onTab={onTab}>
        <p className={styles.paragraph}>
          {t(B.song1, lang)}
          <Ref n={2} />
        </p>
        {/* La citation ne se traduit pas : c'est la source primaire, verbatim. */}
        <blockquote className={styles.quote}>
          <p>« {t(S.wiki.song.lyrics, lang)} »</p>
          <cite className={styles.cite}>{t(S.wiki.song.lyrics_note, lang)}</cite>
        </blockquote>
        <p className={styles.paragraph}>
          {t(B.song2, lang)}
          <Ref n={3} />
        </p>

        <div className={styles.mediaBox}>
          <p className={styles.fileLine}>
            {t(S.wiki.song.file, lang)}{' '}
            <span className={styles.fileName}>{t(S.wiki.song.filename, lang)}</span>
            <span className={styles.fileState}>{mixer.stateLabel}</span>
          </p>
          <VuMeter className={styles.vu} barClassName={styles.vuBar} />
          <div className={styles.formRow}>
            <button type="button" className={styles.button} onClick={onToggleAudio}>
              {audio.label}
            </button>
            <button type="button" className={styles.button} onClick={mixer.onToggleHold}>
              {mixer.holdLabel}
            </button>
            <button type="button" className={styles.button} onClick={mixer.onStop}>
              {t(S.wiki.song.stop, lang)}
            </button>
          </div>
          <div className={styles.tableScroll}>
            <table className={styles.mixTable}>
              <tbody>
                {mixer.sliders.map((slider) => (
                  <tr key={slider.key}>
                    <th scope="row">
                      <label htmlFor={`wiki-${slider.key}`}>{slider.label}</label>
                    </th>
                    <td>
                      <input
                        id={`wiki-${slider.key}`}
                        type="range"
                        className={styles.slider}
                        min={slider.min}
                        max={slider.max}
                        step={slider.step}
                        value={slider.value}
                        onChange={(event) => {
                          slider.onChange(event.currentTarget.valueAsNumber);
                        }}
                      />
                    </td>
                    <td className={styles.mixValue}>{slider.valueLabel}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={styles.formRow}>
            {mixer.presets.map((preset) => (
              <button key={preset.name} type="button" className={styles.button} onClick={preset.onSelect}>
                {preset.label}
              </button>
            ))}
            <button type="button" className={styles.button} onClick={mixer.onToggleWobble}>
              {mixer.wobbleLabel}
            </button>
          </div>
          <p className={styles.disclaimer}>{t(S.wiki.song.disclaimer, lang)}</p>
        </div>

        <h3 className={styles.h3}>{t(S.wiki.sections.excerpts, lang)}</h3>
        <p className={styles.paragraph}>{t(B.excerpts1, lang)}</p>
        <div className={styles.tableScroll}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>{t(S.wiki.song.excerpt, lang)}</th>
                <th>{t(S.wiki.song.position, lang)}</th>
                <th>{t(S.wiki.song.listen, lang)}</th>
              </tr>
            </thead>
            <tbody>
              {mixer.pads.map((pad) => (
                <tr key={pad.index}>
                  <td>{pad.label}</td>
                  <td className={styles.mono}>{pad.timeLabel}</td>
                  <td>
                    <button type="button" className={styles.smallLink} onClick={pad.onHit}>
                      ▶ {t(S.wiki.song.play, lang)}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className={styles.disclaimer} role="status">
          {audio.hint}
        </p>
      </Section>

      <h2 className={styles.h2} id="notes">
        {t(S.wiki.sections.notes, lang)}
      </h2>
      <ol className={styles.notes}>
        {[S.wiki.notes.n1, S.wiki.notes.n2, S.wiki.notes.n3, S.wiki.notes.n4].map((note, index) => (
          <li key={index} id={`note-${String(index + 1)}`}>
            <span className={styles.noteArrow} aria-hidden="true">
              ↑{' '}
            </span>
            {t(note, lang)}
          </li>
        ))}
      </ol>

      <h2 className={styles.h2}>{t(S.wiki.sections.see_also, lang)}</h2>
      <ul className={styles.seeAlso}>
        <li>
          <button type="button" className={styles.link} onClick={onLeave}>
            {t(S.wiki.see_also.a, lang)}
          </button>{' '}
          <span className={styles.aside}>({t(S.wiki.see_also.a_note, lang)})</span>
        </li>
        <li>
          <RedLink title={t(S.wiki.marks.missing, lang)}>{t(S.wiki.see_also.b, lang)}</RedLink>{' '}
          <span className={styles.aside}>({t(S.wiki.see_also.to_create, lang)})</span>
        </li>
        <li>
          <RedLink title={t(S.wiki.marks.missing, lang)}>{t(S.wiki.see_also.c, lang)}</RedLink>{' '}
          <span className={styles.aside}>({t(S.wiki.see_also.to_create, lang)})</span>
        </li>
        <li>
          <button type="button" className={styles.link} onClick={onLeave}>
            {t(S.wiki.see_also.d, lang)}
          </button>
        </li>
      </ul>

      <div className={styles.categories}>
        <strong>{t(S.wiki.categories.label, lang)} :</strong>{' '}
        {tList(S.wiki.categories.items, lang).map((item, index) => (
          <span key={item}>
            {index > 0 && <span className={styles.catSep}> | </span>}
            <button type="button" className={styles.link} onClick={onLeave}>
              {item}
            </button>
          </span>
        ))}
      </div>
    </>
  );
}

/** Une ligne de l'infobox : libellé sur fond gris, valeur à droite. */
function InfoRow({ k, v }: { readonly k: string; readonly v: ReactNode }): ReactNode {
  return (
    <div className={styles.infoRow}>
      <span className={styles.infoKey}>{k}</span>
      <span className={styles.infoValue}>{v}</span>
    </div>
  );
}

/** Un sous-en-tête d'infobox. */
function InfoSection({ label }: { readonly label: string }): ReactNode {
  return <div className={styles.infoSection}>{label}</div>;
}

interface SectionProps {
  readonly id: string;
  readonly title: string;
  readonly lang: 'fr' | 'en';
  readonly onTab: (tab: Tab) => void;
  readonly children: ReactNode;
}

/** Un `<h2>` de section, avec ses deux liens « modifier » à droite. */
function Section({ id, title, lang, onTab, children }: SectionProps): ReactNode {
  return (
    <section className={styles.section}>
      <h2 className={styles.h2} id={id}>
        {title}
        <span className={styles.sectionEdit}>
          [
          <button type="button" className={styles.smallLink} onClick={() => { onTab('edit'); }}>
            {t(S.wiki.sections.edit, lang)}
          </button>
          {' | '}
          <button type="button" className={styles.smallLink} onClick={() => { onTab('edit'); }}>
            {t(S.wiki.sections.edit_source, lang)}
          </button>
          ]
        </span>
      </h2>
      {children}
    </section>
  );
}

/** Le sommaire numéroté, replié comme sur une vraie page. */
function Toc({ lang }: { readonly lang: 'fr' | 'en' }): ReactNode {
  const [open, setOpen] = useState(true);
  const entries: readonly { n: string; label: string; id: string; sub?: boolean }[] = [
    { n: '1', label: t(S.wiki.sections.etymology, lang), id: 'etymology' },
    { n: '2', label: t(S.wiki.sections.description, lang), id: 'description' },
    { n: '2.1', label: t(S.wiki.sections.arms_legs, lang), id: 'description', sub: true },
    { n: '2.2', label: t(S.wiki.sections.colour, lang), id: 'description', sub: true },
    { n: '2.3', label: t(S.wiki.sections.eyes, lang), id: 'description', sub: true },
    { n: '2.4', label: t(S.wiki.sections.beauty, lang), id: 'description', sub: true },
    { n: '3', label: t(S.wiki.sections.locomotion, lang), id: 'locomotion' },
    { n: '3.1', label: t(S.wiki.sections.land_swim, lang), id: 'locomotion', sub: true },
    { n: '3.2', label: t(S.wiki.sections.water_walk, lang), id: 'locomotion', sub: true },
    { n: '4', label: t(S.wiki.sections.smell, lang), id: 'smell' },
    { n: '5', label: t(S.wiki.sections.affection, lang), id: 'affection' },
    { n: '6', label: t(S.wiki.sections.song, lang), id: 'song' },
    { n: '7', label: t(S.wiki.sections.notes, lang), id: 'notes' },
  ];

  return (
    <nav className={styles.toc} aria-label={t(S.wiki.sections.toc, lang)}>
      <div className={styles.tocHead}>
        <strong>{t(S.wiki.sections.toc, lang)}</strong>{' '}
        <button
          type="button"
          className={styles.smallLink}
          aria-expanded={open}
          onClick={() => {
            setOpen((value) => !value);
          }}
        >
          [{t(S.wiki.sections.toc_hide, lang)}]
        </button>
      </div>
      {open && (
        <ol className={styles.tocList}>
          {entries.map((entry) => (
            <li key={entry.n} className={cx(entry.sub === true && styles.tocSub)}>
              <span className={styles.tocNum}>{entry.n}</span>
              <a className={styles.link} href={`#${entry.id}`}>
                {entry.label}
              </a>
            </li>
          ))}
        </ol>
      )}
    </nav>
  );
}
